import { useCallback, useSyncExternalStore } from 'react'

export function useMediaQuery(query: string): boolean {
  const getSnapshot = useCallback(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches
    }
    return false
  }, [query])

  const subscribe = useCallback((onStoreChange: () => void) => {
    if (typeof window === 'undefined') return () => {}

    const mediaQueryList = window.matchMedia(query)

    mediaQueryList.addEventListener('change', onStoreChange)
    return () => {
      mediaQueryList.removeEventListener('change', onStoreChange)
    }
  }, [query])

  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}

export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1024px)')
}

export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)')
}

export function useIsTablet(): boolean {
  return useMediaQuery('(min-width: 768px) and (max-width: 1023px)')
}

// Ask Hopscotch docking rule (companion-specific — the spec's push
// threshold is ~1200px, wider than the app's 1024px desktop breakpoint;
// do NOT fold this into useIsDesktop):
//   push    — desktop: the open panel is a flex sibling, content reflows
//   overlay — tablet/narrow desktop: panel overlays a dimmed app
//   sheet   — phone: full-height bottom sheet
export type CompanionMode = 'push' | 'overlay' | 'sheet'

export function useCompanionMode(): CompanionMode {
  const push = useMediaQuery('(min-width: 1200px)')
  const sheet = useMediaQuery('(max-width: 767px)')
  return push ? 'push' : sheet ? 'sheet' : 'overlay'
}
